/**
 * Trial Expiration Screen
 * Displays when user's trial has expired
 * Based on PendingApprovalScreen design for consistency
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { useOrganization } from '../contexts/OrganizationContext';
import { useTrial } from '../contexts/TrialContext';
import PlatformLayout from './PlatformLayout';
import PlatformButton from './PlatformButton';
import PlatformText from './PlatformText';
import { formatTrialDate, isSubscriptionExpired } from '../utils/trialUtils';
import { Capacitor } from '@capacitor/core';
import './TrialExpirationScreen.css';

const TrialExpirationScreen = () => {
  const navigate = useNavigate();
  const { trialStatus } = useTrial();
  const { userRole, organizationData } = useOrganization();
  
  // Platform detection
  const isNative = Capacitor.isNativePlatform();
  
  // Determine if this is subscription expiration or trial expiration
  const subscriptionStatus = trialStatus?.subscriptionStatus || organizationData?.subscriptionStatus;
  const subscriptionEndsAt = organizationData?.subscriptionEndsAt || null;
  const isSubscriptionExpiredResult = isSubscriptionExpired(subscriptionStatus, subscriptionEndsAt);
  
  // Check if it's subscription expiration (not trial expiration)
  const isSubscriptionExpiredType = subscriptionStatus === 'expired' || 
    (subscriptionStatus === 'active' && subscriptionEndsAt && isSubscriptionExpiredResult);
  
  // Determine title and message based on expiration type
  const expirationTitle = isSubscriptionExpiredType ? 'Subscription Expired' : 'Trial Period Expired';
  const expirationMessage = isSubscriptionExpiredType
    ? 'Your subscription has expired. To continue using the application, please upgrade to a paid subscription plan.'
    : 'Your 30-day free trial has ended. To continue using the application, please upgrade to a paid subscription plan.';

  const handleSignOut = async () => {
    await auth.signOut();
    navigate('/');
  };

  const handleUpgrade = () => {
    // Use replace: true to prevent back button from accessing blocked routes
    // This ensures users can't navigate back to restricted pages when trial is expired
    navigate('/account-plan', { replace: true });
  };

  const handleContactSupport = () => {
    // TODO: Open support contact
  };

  return (
    <PlatformLayout>
      <div className="trial-expiration-container">
        <div className="trial-expiration-card">
          <div className="trial-expiration-icon">
            <PlatformText variant="h1">⏰</PlatformText>
          </div>
          
          <PlatformText variant="h2" className="trial-expiration-title">
            {expirationTitle}
          </PlatformText>
          
          <div className="trial-expiration-message">
            <PlatformText variant="body" className="trial-expiration-main-message">
              {expirationMessage}
            </PlatformText>
            
            {/* Show trial period details for trial expiration */}
            {!isSubscriptionExpiredType && trialStatus && (
              <div className="trial-expiration-details">
                <PlatformText variant="caption" className="trial-detail-label">
                  Trial Period:
                </PlatformText>
                <PlatformText variant="body" className="trial-detail-value">
                  {formatTrialDate(trialStatus.trialStartDate)} - {formatTrialDate(trialStatus.trialEndDate)}
                </PlatformText>
              </div>
            )}
            
            {/* Show subscription end date for subscription expiration */}
            {isSubscriptionExpiredType && subscriptionEndsAt && (
              <div className="trial-expiration-details">
                <PlatformText variant="caption" className="trial-detail-label">
                  Subscription Ended:
                </PlatformText>
                <PlatformText variant="body" className="trial-detail-value">
                  {(() => {
                    let expiryDate;
                    if (typeof subscriptionEndsAt === 'string') {
                      expiryDate = new Date(subscriptionEndsAt);
                    } else if (subscriptionEndsAt && typeof subscriptionEndsAt.toDate === 'function') {
                      expiryDate = subscriptionEndsAt.toDate();
                    } else if (subscriptionEndsAt instanceof Date) {
                      expiryDate = subscriptionEndsAt;
                    } else {
                      return 'N/A';
                    }
                    return formatTrialDate(expiryDate);
                  })()}
                </PlatformText>
              </div>
            )}
          </div>

          <div className="trial-expiration-benefits">
            <PlatformText variant="h3" className="benefits-title">
              Upgrade Benefits:
            </PlatformText>
            <ul className="benefits-list">
              <li>
                <PlatformText variant="body">
                  ✅ Unlimited access to all features
                </PlatformText>
              </li>
              <li>
                <PlatformText variant="body">
                  ✅ Priority customer support
                </PlatformText>
              </li>
              <li>
                <PlatformText variant="body">
                  ✅ Advanced analytics and reporting
                </PlatformText>
              </li>
              <li>
                <PlatformText variant="body">
                  ✅ Data backup and security
                </PlatformText>
              </li>
            </ul>
          </div>

          {/* Native Platform - Informational note */}
          {isNative && (
            <div className="trial-expiration-website-info">
              <PlatformText variant="body" className="trial-website-info-text">
                This app does not offer subscription changes. Please use the web portal associated with your account to manage your organization’s plan.
              </PlatformText>
            </div>
          )}

          <div className="trial-expiration-actions">
            {!isNative && userRole === 'BusinessOwner' && (
              <PlatformButton 
                onClick={handleUpgrade}
                className="trial-upgrade-button"
                variant="primary"
              >
                View Subscription Plans
              </PlatformButton>
            )}
            
            <PlatformButton 
              onClick={handleSignOut}
              className="trial-signout-button"
              variant="secondary"
            >
              Sign Out
            </PlatformButton>
          </div>

          <div className="trial-expiration-footer">
            <PlatformText variant="caption" className="trial-footer-text">
              Need help? Our support team is here to assist you.
            </PlatformText>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
};

export default TrialExpirationScreen;
